import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import ProfileSection from './components/ProfileSection';
import SystemPreferencesSection from './components/SystemPreferencesSection';
import APIConfigurationSection from './components/APIConfigurationSection';
import SecuritySection from './components/SecuritySection';
import TeamManagementSection from './components/TeamManagementSection';
import AuditSection from './components/AuditSection';
import {
  UserProfile,
  SystemPreferences,
  APIConfiguration,
  SecuritySettings,
  TeamMember,
  AuditSettings,
  SettingsTab } from
'./types';

const UserProfileAndSystemSettings = () => {
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  // Convert teamMembers to state to persist changes
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([
    {
      id: '1',
      name: 'Carlos Eduardo Santos',
      email: 'carlos.santos@empresa.com.br',
      role: 'supervisor',
      status: 'active',
      lastActive: new Date(Date.now() - 1800000),
      consultationsCount: 342,
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1c7eb896f-1764929472139.png"
    },
    {
      id: '2',
      name: 'Maria Fernanda Oliveira',
      email: 'maria.oliveira@empresa.com.br',
      role: 'analyst',
      status: 'active',
      lastActive: new Date(Date.now() - 600000),
      consultationsCount: 567,
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1c7eb896f-1764929472139.png"
    },
    {
      id: '3',
      name: 'João Pedro Costa',
      email: 'joao.costa@empresa.com.br',
      role: 'analyst',
      status: 'active',
      lastActive: new Date(Date.now() - 3600000),
      consultationsCount: 423,
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_177dea4ae-1764780932950.png"
    },
    {
      id: '4',
      name: 'Juliana Rodrigues',
      email: 'juliana.rodrigues@empresa.com.br',
      role: 'viewer',
      status: 'inactive',
      lastActive: new Date(Date.now() - 86400000),
      consultationsCount: 89,
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1f151a97d-1763301606711.png"
    }
  ]);

  const currentUser: UserProfile = {
    id: '1',
    name: 'Ana Paula Silva',
    email: 'ana.silva@empresa.com.br',
    role: 'admin',
    department: 'tax',
    avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
    phone: '(11) 98765-4321',
    lastLogin: new Date(Date.now() - 3600000),
    createdAt: new Date('2023-01-15'),
    permissions: [
    {
      id: '1',
      module: 'Consultas',
      canView: true,
      canCreate: true,
      canEdit: true,
      canDelete: true,
      canExport: true
    },
    {
      id: '2',
      module: 'Configurações',
      canView: true,
      canCreate: true,
      canEdit: true,
      canDelete: false,
      canExport: true
    }],

    notificationPreferences: {
      emailNotifications: true,
      smsNotifications: false,
      consultationAlerts: true,
      systemUpdates: true,
      weeklyReports: true,
      apiErrorAlerts: true
    }
  };

  const systemPreferences: SystemPreferences = {
    dashboardLayout: 'detailed',
    defaultConsultationView: 'table',
    itemsPerPage: 25,
    dateFormat: 'DD/MM/YYYY',
    exportFormat: 'xlsx',
    autoSaveInterval: 5,
    theme: 'light'
  };

  const apiConfiguration: APIConfiguration = {
    environment: 'production',
    baseUrl: 'https://api.gestortributario.com.br/v3',
    timeout: 30,
    retryAttempts: 3,
    rateLimitPerMinute: 60,
    connectionStatus: 'connected',
    lastSync: new Date(Date.now() - 300000),
    tokenExpiresAt: new Date(Date.now() + 86400000),
    autoRenewToken: true
  };

  const securitySettings: SecuritySettings = {
    sessionTimeout: 30,
    requireTwoFactor: false,
    passwordExpiryDays: 90,
    maxLoginAttempts: 5,
    ipWhitelist: ['192.168.1.100', '192.168.1.101', '10.0.0.50'],
    allowRemoteAccess: true
  };

  const auditSettings: AuditSettings = {
    retentionDays: 365,
    logLevel: 'standard',
    trackUserActivity: true,
    trackAPIRequests: true,
    trackDataChanges: true,
    exportAuditLogs: true
  };

  const handleProfileUpdate = (profile: Partial<UserProfile>) => {
    console.log('Atualizando perfil:', profile);
  };

  const handleSystemPreferencesUpdate = (preferences: SystemPreferences) => {
    console.log('Atualizando preferências do sistema:', preferences);
  };

  const handleAPIConfigUpdate = (config: APIConfiguration) => {
    console.log('Atualizando configuração de API:', config);
  };

  const handleSecurityUpdate = (settings: SecuritySettings) => {
    console.log('Atualizando configurações de segurança:', settings);
  };

  const handleTeamUpdate = (members: TeamMember[]) => {
    console.log('Atualizando equipe:', members);
    setTeamMembers(members);
  };

  const handleAuditUpdate = (settings: AuditSettings) => {
    console.log('Atualizando configurações de auditoria:', settings);
  };

  const tabs = [
  { id: 'profile' as const, label: 'Perfil', icon: 'User' },
  { id: 'system' as const, label: 'Sistema', icon: 'Settings' },
  { id: 'api' as const, label: 'API', icon: 'Server' },
  { id: 'security' as const, label: 'Segurança', icon: 'Shield' },
  { id: 'team' as const, label: 'Equipe', icon: 'Users' },
  { id: 'audit' as const, label: 'Auditoria', icon: 'FileText' }];


  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.altKey && e.key >= '1' && e.key <= '6') {
        e.preventDefault();
        const tabIndex = parseInt(e.key) - 1;
        if (tabs[tabIndex]) {
          setActiveTab(tabs[tabIndex].id);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  return (
    <>
      <Helmet>
        <title>Configurações - TaxQuery Pro</title>
        <meta
          name="description"
          content="Gerencie seu perfil, preferências do sistema e configurações de segurança no TaxQuery Pro" />

      </Helmet>

      <div className="min-h-screen bg-background">
        <Sidebar isCollapsed={isSidebarCollapsed} onCollapsedChange={setIsSidebarCollapsed} />
        <Header title="Configurações" onUserMenuClick={() => {}} />

        <main
          className={`pt-16 transition-all duration-300 ${
          isSidebarCollapsed ? 'lg:pl-20' : 'lg:pl-64'}`
          }>

          <div className="p-6 max-w-7xl mx-auto">
            <div className="bg-card rounded-lg border border-border mb-6">
              <nav className="flex overflow-x-auto" role="tablist">
                {tabs.map((tab, index) =>
                <button
                  key={tab.id}
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  aria-controls={`panel-${tab.id}`}
                  aria-label={`${tab.label} (Alt+${index + 1})`}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                      flex items-center gap-2 px-6 py-4 border-b-2 transition-all duration-150 whitespace-nowrap
                      ${
                  activeTab === tab.id ?
                  'border-primary text-primary font-medium' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'}
                    `
                  }>

                    <Icon name={tab.icon} size={18} color="currentColor" />
                    <span>{tab.label}</span>
                  </button>
                )}
              </nav>
            </div>

            <div className="bg-card rounded-lg border border-border p-6">
              {activeTab === 'profile' &&
              <ProfileSection profile={currentUser} onUpdate={handleProfileUpdate} />
              }
              {activeTab === 'system' &&
              <SystemPreferencesSection
                preferences={systemPreferences}
                onUpdate={handleSystemPreferencesUpdate} />

              }
              {activeTab === 'api' &&
              <APIConfigurationSection
                config={apiConfiguration}
                onUpdate={handleAPIConfigUpdate}
                isAdmin={currentUser.role === 'admin'} />

              }
              {activeTab === 'security' &&
              <SecuritySection
                settings={securitySettings}
                onUpdate={handleSecurityUpdate}
                isAdmin={currentUser.role === 'admin'} />

              }
              {activeTab === 'team' &&
              <TeamManagementSection
                members={teamMembers}
                onUpdate={handleTeamUpdate}
                isAdmin={currentUser.role === 'admin' || currentUser.role === 'supervisor'} />

              }
              {activeTab === 'audit' &&
              <AuditSection
                settings={auditSettings}
                onUpdate={handleAuditUpdate}
                isAdmin={currentUser.role === 'admin'} />

              }
            </div>
          </div>
        </main>
      </div>
    </>);

};

export default UserProfileAndSystemSettings;